import React, { useState } from "react";
import { FaSearch, FaChevronDown, FaChevronUp, FaPhone, FaEnvelope, FaWhatsapp, FaStar, FaShippingFast, FaUtensils, FaCreditCard, FaUser, FaMobileAlt } from "react-icons/fa";
import { MdFastfood, MdLocalShipping, MdPayment, MdSupportAgent, MdSecurity } from "react-icons/md";

const FAQKnowledgeBase = () => {
    const [activeCategory, setActiveCategory] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedFAQs, setExpandedFAQs] = useState([]);
    const [recentSearches, setRecentSearches] = useState(["Delivery Time", "Refund", "Order Tracking"]);
    const [showEmailRequest, setShowEmailRequest] = useState(null);
    const [emailRequestData, setEmailRequestData] = useState({
        question: "",
        email: "",
        additionalDetails: ""
    });

    const categories = [
        { id: "all", name: "All FAQs", icon: <FaStar />, color: "#FF6B35", count: 36 },
        { id: "ordering", name: "Ordering", icon: <MdFastfood />, color: "#06D6A0", count: 8 },
        { id: "delivery", name: "Delivery", icon: <MdLocalShipping />, color: "#1A8FE3", count: 12 },
        { id: "payment", name: "Payments", icon: <MdPayment />, color: "#FFD166", count: 6 },
        { id: "account", name: "Account", icon: <FaUser />, color: "#9D4EDD", count: 5 },
        { id: "app", name: "App Issues", icon: <FaMobileAlt />, color: "#EF476F", count: 5 },
    ];

    const faqs = [
        // Ordering FAQs
        { id: 1, question: "How do I place an order?", answer: "You can place an order through our mobile app or website. Simply browse restaurants, select items, add them to your cart, and proceed to checkout. You'll need to provide delivery address and payment details.", category: "ordering", tags: ["ordering", "basics"], popular: true },
        { id: 2, question: "Can I schedule an order for later?", answer: "Yes! You can schedule orders for up to 3 days in advance. During checkout, select the 'Schedule for later' option and choose your preferred delivery time.", category: "ordering", tags: ["schedule", "timing"], popular: false },
        { id: 3, question: "How do I modify or cancel my order?", answer: "You can modify or cancel your order within 5 minutes of placing it. Go to 'My Orders' → select the order → click 'Modify' or 'Cancel'. After 5 minutes, please contact support.", category: "ordering", tags: ["modify", "cancel"], popular: true },

        // Delivery FAQs
        { id: 4, question: "What are the delivery charges?", answer: "Delivery charges vary by restaurant and distance. They range from ₹20 to ₹60. Some restaurants offer free delivery on orders above ₹300. Charges are clearly shown before checkout.", category: "delivery", tags: ["charges", "fees"], popular: true },
        { id: 5, question: "How long does delivery take?", answer: "Delivery typically takes 30-45 minutes. This depends on restaurant preparation time, traffic, and your location. You can track your order in real-time on the app.", category: "delivery", tags: ["time", "tracking"], popular: true },
        { id: 6, question: "Can I track my delivery partner?", answer: "Yes! Once your order is out for delivery, you can track your delivery partner's live location on the map in real-time. You'll also receive ETA updates.", category: "delivery", tags: ["tracking", "live"], popular: false },

        // Payment FAQs
        { id: 7, question: "What payment methods do you accept?", answer: "We accept UPI, credit/debit cards, net banking, wallets (Paytm, PhonePe), cash on delivery, and food credits. All payments are secure and encrypted.", category: "payment", tags: ["payments", "methods"], popular: true },
        { id: 8, question: "Is cash on delivery available?", answer: "Yes, cash on delivery is available for most restaurants. However, some restaurants may require prepayment. This will be clearly indicated during checkout.", category: "payment", tags: ["COD", "cash"], popular: false },
        { id: 9, question: "How do I apply a promo code?", answer: "During checkout, look for the 'Apply Promo Code' field. Enter your code and click apply. The discount will be reflected in your total immediately.", category: "payment", tags: ["promo", "discount"], popular: true },

        // Account FAQs
        { id: 10, question: "How do I reset my password?", answer: "Go to 'Account' → 'Security' → 'Change Password'. You'll need to verify via OTP sent to your registered mobile number or email.", category: "account", tags: ["password", "security"], popular: false },
        { id: 11, question: "How do I update my delivery address?", answer: "Navigate to 'Account' → 'Addresses' → 'Add New Address' or edit existing ones. You can save multiple addresses for quick ordering.", category: "account", tags: ["address", "profile"], popular: false },

        // App Issues
        { id: 12, question: "The app is crashing. What should I do?", answer: "1. Force close and restart the app\n2. Check for updates in Play Store/App Store\n3. Clear app cache in settings\n4. Reinstall the app\nIf issues persist, contact support.", category: "app", tags: ["crash", "technical"], popular: true },
    ];

    const contactOptions = [
        { icon: <FaPhone />, title: "Call Support", details: "24/7 Available", action: "Call Now", color: "#FF6B35" },
        { icon: <FaWhatsapp />, title: "WhatsApp", details: "Quick Response", action: "Chat Now", color: "#25D366" },
        { icon: <FaEnvelope />, title: "Email", details: "Within 2 hours", action: "Send Email", color: "#1A8FE3" },
    ];

    const toggleFAQ = (id) => {
        setExpandedFAQs(prev =>
            prev.includes(id) ? prev.filter(faqId => faqId !== id) : [...prev, id]
        );
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
        if (query && !recentSearches.includes(query)) {
            setRecentSearches(prev => [query, ...prev.slice(0, 2)]);
        }
    };

    const handleEmailRequest = (faqId, question) => {
        setShowEmailRequest(faqId);
        setEmailRequestData({
            question: question,
            email: "",
            additionalDetails: ""
        });
    };

    const handleEmailSubmit = (e) => {
        e.preventDefault();
        alert(`Email request sent for: "${emailRequestData.question}"\nWe will respond to ${emailRequestData.email} within 2 hours.`);
        setShowEmailRequest(null);
        setEmailRequestData({
            question: "",
            email: "",
            additionalDetails: ""
        });
    };

    const filteredFAQs = faqs.filter(faq => {
        const matchesCategory = activeCategory === "all" || faq.category === activeCategory;
        const matchesSearch = searchQuery === "" ||
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    const getCategoryCount = (categoryId) => {
        if (categoryId === "all") return faqs.length;
        return faqs.filter(faq => faq.category === categoryId).length;
    };

    const styles = {
        container: {
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "20px",
            fontFamily: "'Poppins', 'Segoe UI', sans-serif",
            paddingTop: window.innerWidth < 500 ? "0px" : " ",
            paddingLeft: window.innerWidth < 500 ? "0px" : " ",
            paddingRight: window.innerWidth < 500 ? "25px" : " ",
        },
        header: {
            background: "linear-gradient(135deg, #FF6B35 0%, #FFA62E 100%)",
            borderRadius: "20px",
            padding: "30px",
            marginBottom: "30px",
            color: "white",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(255, 107, 53, 0.3)"
        },
        headerTitle: {
            fontSize: "32px",
            fontWeight: "700",
            margin: "0 0 10px 0",
            letterSpacing: "-0.5px"
        },
        headerSubtitle: {
            fontSize: "16px",
            opacity: "0.9",
            maxWidth: "600px",
            margin: "0 auto"
        },
        searchContainer: {
            marginBottom: "20px",
            position: "relative"
        },
        searchInput: {
            width: "100%",
            padding: "10px",
            border: "2px solid #e0e0e0",
            borderRadius: "15px",
            fontSize: "16px",
            outline: "none",
            transition: "all 0.3s ease",
            boxShadow: "0 5px 20px rgba(0,0,0,0.05)"
        },
        searchInputFocus: {
            borderColor: "#FF6B35",
            boxShadow: "0 5px 25px rgba(255, 107, 53, 0.2)"
        },
        searchIcon: {
            position: "absolute",
            right: "20px",
            top: "33%",
            transform: "translateY(-50%)",
            fontSize: "20px",
            color: "#FF6B35"
        },
        recentSearches: {
            display: "flex",
            gap: "10px",
            marginTop: "15px",
            flexWrap: "wrap"
        },
        recentTag: {
            background: "#f8f9fa",
            padding: "6px 15px",
            borderRadius: "20px",
            fontSize: "13px",
            color: "#666",
            cursor: "pointer",
            transition: "all 0.3s ease",
            border: "1px solid #e0e0e0"
        },
        recentTagHover: {
            background: "#FF6B35",
            color: "white",
            borderColor: "#FF6B35"
        },
        categoriesContainer: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "15px",
            marginBottom: "40px"
        },
        categoryCard: {
            background: "white",
            borderRadius: "15px",
            padding: "20px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            border: "2px solid #f0f0f0",
            textAlign: "center",
            position: "relative",
            overflow: "hidden"
        },
        categoryCardActive: {
            borderColor: "#FF6B35",
            background: "rgba(255, 107, 53, 0.05)",
            transform: "translateY(-5px)",
            boxShadow: "0 10px 30px rgba(255, 107, 53, 0.15)"
        },
        categoryIcon: {
            fontSize: "30px",
            marginBottom: "15px",
            width: "60px",
            height: "60px",
            borderRadius: "15px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
            color: "white"
        },
        categoryName: {
            fontSize: "16px",
            fontWeight: "600",
            color: "#333",
            marginBottom: "5px"
        },
        categoryCount: {
            fontSize: "12px",
            color: "#666",
            background: "#f8f9fa",
            padding: "3px 10px",
            borderRadius: "10px",
            display: "inline-block"
        },
        popularTag: {
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "#FF6B35",
            color: "white",
            fontSize: "10px",
            fontWeight: "600",
            padding: "3px 8px",
            borderRadius: "10px"
        },
        faqsContainer: {
            background: "white",
            borderRadius: "20px",
            padding: "30px",
            // boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
            border: "1px solid #f0f0f0",
            marginBottom: "20px",
            paddingBottom:"0px",
            
        },
        sectionTitle: {
            fontSize: "20px",
            fontWeight: "600",
            color: "#333",
            marginBottom: "10px",
            display: "flex",
            alignItems: "center",
            gap: "10px"
        },
        faqItem: {
            borderTop: "1px solid #f0f0f0",
            transition: "all 0.3s ease"
        },
        faqQuestion: {
            padding: "20px 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            transition: "all 0.3s ease"
        },
        faqQuestionText: {
            fontSize: "16px",
            fontWeight: "500",
            color: "#333",
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: "15px"
        },
        faqIcon: {
            color: "#FF6B35",
            fontSize: "18px",
            minWidth: "24px"
        },
        faqArrow: {
            fontSize: "14px",
            color: "#999",
            transition: "transform 0.3s ease",
            minWidth: "20px"
        },
        faqAnswer: {
            padding: "0 0 20px 0",
            color: "#666",
            lineHeight: "1.6",
            fontSize: "15px",
            whiteSpace: "pre-line"
        },
        tagsContainer: {
            display: "flex",
            gap: "8px",
            marginTop: "15px",
            flexWrap: "wrap"
        },
        tag: {
            background: "#f8f9fa",
            padding: "4px 12px",
            borderRadius: "15px",
            fontSize: "12px",
            color: "#666",
            border: "1px solid #e0e0e0"
        },
        emailRequestForm: {
            background: "#f8f9fa",
            borderRadius: "10px",
            padding: "20px",
            marginTop: "15px",
            border: "1px solid #e0e0e0"
        },
        emailRequestTitle: {
            fontSize: "16px",
            fontWeight: "600",
            color: "#333",
            marginBottom: "15px",
            display: "flex",
            alignItems: "center",
            gap: "8px"
        },
        formGroup: {
            marginBottom: "15px"
        },
        label: {
            display: "block",
            marginBottom: "5px",
            fontSize: "14px",
            fontWeight: "500",
            color: "#555"
        },
        input: {
            width: "100%",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            fontSize: "14px"
        },
        textarea: {
            width: "100%",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "5px",
            fontSize: "14px",
            resize: "vertical",
            minHeight: "80px"
        },
        formButtons: {
            display: "flex",
            gap: "10px",
            marginTop: "20px"
        },
        submitButton: {
            background: "#FF6B35",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "5px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "background 0.3s"
        },
        cancelButton: {
            background: "#6c757d",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "5px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "background 0.3s"
        },
        requestEmailButton: {
            background: "transparent",
            color: "#FF6B35",
            border: "1px solid #FF6B35",
            padding: "8px 15px",
            borderRadius: "5px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.3s",
            marginTop: "15px",
            display: "flex",
            alignItems: "center",
            gap: "8px"
        },
        noResults: {
            textAlign: "center",
            padding: "50px 20px"
        },
        noResultsIcon: {
            fontSize: "60px",
            color: "#e0e0e0",
            marginBottom: "20px"
        },
        contactSection: {
            background: "linear-gradient(135deg, #06D6A0 0%, #4ECDC4 100%)",
            borderRadius: "20px",
            padding: "30px",
            color: "white",
            marginBottom: "40px",
            boxShadow: "0 10px 30px rgba(6, 214, 160, 0.3)"
        },
        contactGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
            gap: "20px",
            marginTop: "25px"
        },
        contactCard: {
            background: "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "15px",
            padding: "25px",
            textAlign: "center",
            transition: "all 0.3s ease",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            cursor: "pointer"
        },
        contactCardHover: {
            background: "rgba(255, 255, 255, 0.25)",
            transform: "translateY(-5px)"
        },
        contactIcon: {
            fontSize: "35px",
            marginBottom: "15px"
        },
        contactTitle: {
            fontSize: "18px",
            fontWeight: "600",
            marginBottom: "5px"
        },
        contactDetails: {
            fontSize: "14px",
            opacity: "0.9",
            marginBottom: "15px"
        },
        contactAction: {
            background: "white",
            color: "#06D6A0",
            border: "none",
            padding: "10px 20px",
            borderRadius: "10px",
            fontWeight: "600",
            fontSize: "14px",
            cursor: "pointer",
            transition: "all 0.3s ease"
        },
        statsContainer: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            marginTop: "00px",
            marginBottom: "00px"
        },
        statCard: {
            background: "white",
            borderRadius: "15px",
            padding: "25px",
            textAlign: "center",
            boxShadow: "0 5px 20px rgba(0,0,0,0.05)",
            border: "1px solid #f0f0f0"
        },
        statNumber: {
            fontSize: "32px",
            fontWeight: "700",
            color: "#FF6B35",
            marginBottom: "10px"
        },
        statLabel: {
            fontSize: "14px",
            color: "#666"
        }
    };

    return (
        <div style={styles.container}>
            {/* Header */}


            {/* Search Bar */}
            <div style={{
                display: "flex",
                justifyContent: "flex-end", // Align to end (right side)
                width: "100%",
                marginBottom: "20px"
            }}>
                <div style={{
                    maxWidth:window.innerWidth<500 ? "100%": "40%", // Maximum 40% width
                    width: "100%", // Full width up to maxWidth
                    display: "flex",
                    justifyContent: "flex-end"
                }}>
                    <input
                        type="text"
                        placeholder="Search for answers..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={{
                            padding: "12px 16px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            fontSize: "14px",
                            width: "100%", // Takes full width of parent container
                            maxWidth: "100%",
                            transition: "border-color 0.3s ease",
                            boxSizing: "border-box"
                        }}
                        onFocus={(e) => e.target.style.borderColor = "#FF6B35"}
                        onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
                    />
                </div>
            </div>

            {/* Categories */}
            <div style={styles.categoriesContainer}>
                {categories.map((category) => (
                    <div
                        key={category.id}
                        style={{
                            ...styles.categoryCard,
                            ...(activeCategory === category.id ? styles.categoryCardActive : {})
                        }}
                        onClick={() => setActiveCategory(category.id)}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = activeCategory === category.id ? "#FF6B35" : "#ddd"}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = activeCategory === category.id ? "#FF6B35" : "#f0f0f0"}
                    >
                        <div
                            style={{
                                ...styles.categoryIcon,
                                background: `linear-gradient(135deg, ${category.color} 0%, ${category.color}99 100%)`
                            }}
                        >
                            {category.icon}
                        </div>
                        <div style={styles.categoryName}>{category.name}</div>
                        <div style={styles.categoryCount}>{getCategoryCount(category.id)} FAQs</div>
                    </div>
                ))}
            </div>

            {/* FAQs List */}
            <div style={styles.faqsContainer}>
                <div style={styles.sectionTitle}>
                    <MdSupportAgent />
                    {activeCategory === "all" ? "All FAQs" : categories.find(c => c.id === activeCategory)?.name}
                    <span style={{ marginLeft: "auto", fontSize: "14px", color: "#666", fontWeight: "normal" }}>
                        {filteredFAQs.length} results
                    </span>
                </div>

                {filteredFAQs.length > 0 ? (
                    filteredFAQs.map((faq) => (
                        <div key={faq.id} style={styles.faqItem}>
                            <div
                                style={styles.faqQuestion}
                                onClick={() => toggleFAQ(faq.id)}
                                onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fa"}
                                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            >
                                <div style={styles.faqQuestionText}>
                                    <span style={styles.faqIcon}>❓</span>
                                    {faq.question}
                                    {faq.popular && (
                                        <span style={{
                                            background: "#FF6B35",
                                            color: "white",
                                            fontSize: "10px",
                                            fontWeight: "600",
                                            padding: "3px 10px",
                                            borderRadius: "10px",
                                            marginLeft: "10px"
                                        }}>
                                            Popular
                                        </span>
                                    )}
                                </div>
                                <div style={{
                                    ...styles.faqArrow,
                                    transform: expandedFAQs.includes(faq.id) ? "rotate(180deg)" : "rotate(0deg)"
                                }}>
                                    <FaChevronDown />
                                </div>
                            </div>

                            {expandedFAQs.includes(faq.id) && (
                                <div style={styles.faqAnswer}>
                                    {faq.answer}
                                    <div style={styles.tagsContainer}>
                                        {faq.tags.map((tag, index) => (
                                            <span key={index} style={styles.tag}>
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Email Request Form */}
                                    {showEmailRequest === faq.id ? (
                                        <div style={styles.emailRequestForm}>
                                            <div style={styles.emailRequestTitle}>
                                                <FaEnvelope /> Request More Details via Email
                                            </div>
                                            <form onSubmit={handleEmailSubmit}>
                                                <div style={styles.formGroup}>
                                                    <label style={styles.label}>Your Question</label>
                                                    <input
                                                        type="text"
                                                        value={emailRequestData.question}
                                                        readOnly
                                                        style={{ ...styles.input, background: "#f0f0f0" }}
                                                    />
                                                </div>
                                                <div style={styles.formGroup}>
                                                    <label style={styles.label}>Your Email Address *</label>
                                                    <input
                                                        type="email"
                                                        required
                                                        value={emailRequestData.email}
                                                        onChange={(e) => setEmailRequestData(prev => ({ ...prev, email: e.target.value }))}
                                                        placeholder="Enter your email address"
                                                        style={styles.input}
                                                    />
                                                </div>
                                                <div style={styles.formGroup}>
                                                    <label style={styles.label}>Additional Details (Optional)</label>
                                                    <textarea
                                                        value={emailRequestData.additionalDetails}
                                                        onChange={(e) => setEmailRequestData(prev => ({ ...prev, additionalDetails: e.target.value }))}
                                                        placeholder="Provide more details about your question..."
                                                        style={styles.textarea}
                                                    />
                                                </div>
                                                <div style={styles.formButtons}>
                                                    <button type="submit" style={styles.submitButton}>
                                                        Send Email Request
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowEmailRequest(null)}
                                                        style={styles.cancelButton}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    ) : (
                                        <button
                                            style={styles.requestEmailButton}
                                            onClick={() => handleEmailRequest(faq.id, faq.question)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = "#FF6B35";
                                                e.currentTarget.style.color = "white";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = "transparent";
                                                e.currentTarget.style.color = "#FF6B35";
                                            }}
                                        >
                                            <FaEnvelope /> Still need help? Request email assistance
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div style={styles.noResults}>
                        <div style={styles.noResultsIcon}>🔍</div>
                        <h3 style={{ color: "#333", marginBottom: "10px" }}>No results found</h3>
                        <p style={{ color: "#666", maxWidth: "400px", margin: "0 auto" }}>
                            Try a different search term or browse by category above.
                        </p>
                    </div>
                )}
            </div>



            {/* Stats */}
            <div style={styles.statsContainer}>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>24/7</div>
                    <div style={styles.statLabel}>Support Available</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>15 min</div>
                    <div style={styles.statLabel}>Average Response Time</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>98%</div>
                    <div style={styles.statLabel}>Issues Resolved</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>4.8 ★</div>
                    <div style={styles.statLabel}>Customer Rating</div>
                </div>
            </div>

            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                .fade-in {
                    animation: fadeIn 0.3s ease-out;
                }
                
                body {
                    background: #f8f9fa;
                }
                
                * {
                    box-sizing: border-box;
                }
                
                @media (max-width: 768px) {
                    .container {
                        padding: 15px;
                    }
                    
                    .categoriesContainer {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    
                    .contactGrid {
                        grid-template-columns: 1fr;
                    }
                    
                    .statsContainer {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    
                    .header {
                        padding: 20px;
                    }
                    
                    .headerTitle {
                        font-size: 24px;
                    }
                    
                    .formButtons {
                        flex-direction: column;
                    }
                    
                    .submitButton, .cancelButton {
                        width: 100%;
                    }
                }
                `}
            </style>
        </div>
    );
};

export default FAQKnowledgeBase;